import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FloppyDisk, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import SkillsChipInput from "@/components/SkillsChipInput";

/**
 * Partial-edit modal for candidate profile fields.
 *
 * Feb 2026 (V1.3 P0) — Recruiters needed a way to correct auto-fill
 * mistakes (e.g. parser picked "RESUME" or "OBJECTIVE" as the name).
 * Backend guards with `PATCH /api/candidates/{cid}` — recruiters can
 * only edit candidates they own or those in the team pool; admins/HMs
 * can edit any candidate in workspace scope.
 */
export default function EditCandidateModal({ open, candidate, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        current_title: "",
        location: "",
        years_experience: 0,
        linkedin: "",
    });
    const [skills, setSkills] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!candidate) return;
        setForm({
            name: candidate.name || "",
            email: candidate.email || "",
            phone: candidate.phone || "",
            current_title: candidate.current_title || "",
            location: candidate.location || "",
            years_experience: candidate.years_experience || 0,
            linkedin: candidate.linkedin || "",
        });
        setSkills(candidate.skills || []);
        setErr("");
    }, [candidate?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open || !candidate) return null;

    const save = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            // Only send fields that actually changed — the backend accepts
            // a partial update.
            const changes = {};
            const cmpVal = (a, b) => (a || "") !== (b || "");
            if (cmpVal(form.name, candidate.name)) changes.name = form.name.trim();
            if (cmpVal(form.email, candidate.email)) changes.email = form.email.trim().toLowerCase();
            if (cmpVal(form.phone, candidate.phone)) changes.phone = form.phone.trim();
            if (cmpVal(form.current_title, candidate.current_title)) changes.current_title = form.current_title.trim();
            if (cmpVal(form.location, candidate.location)) changes.location = form.location.trim();
            if (Number(form.years_experience) !== Number(candidate.years_experience || 0)) {
                changes.years_experience = Number(form.years_experience) || 0;
            }
            if (cmpVal(form.linkedin, candidate.linkedin)) changes.linkedin = form.linkedin.trim();
            // Compare skills as sorted CSV — simple order-agnostic diff.
            const skillsJoin = (arr) => (arr || []).map((s) => s.trim()).filter(Boolean).sort().join("|");
            if (skillsJoin(skills) !== skillsJoin(candidate.skills)) {
                changes.skills = skills.map((s) => s.trim()).filter(Boolean);
            }
            if (Object.keys(changes).length === 0) {
                toast.info("No changes to save.");
                setSaving(false);
                return;
            }
            const { data } = await api.patch(`/candidates/${candidate.id}`, changes);
            toast.success("Candidate details updated.");
            onSaved?.(data);
            onClose?.();
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            data-testid="edit-candidate-modal"
        >
            <div
                className="absolute inset-0 bg-ink/50 backdrop-blur-sm"
                onClick={onClose}
            />
            <div className="relative z-10 w-full max-w-xl mx-4 max-h-[92vh] overflow-y-auto bg-white border border-[#0A0A0A] shadow-2xl">
                <div className="p-5 border-b border-[#E5E5E5] flex items-start justify-between gap-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1 inline-flex items-center gap-1.5">
                            <PencilSimple size={11} weight="bold" /> Edit candidate
                        </p>
                        <h2 className="font-heading text-2xl font-black tracking-tight">
                            {candidate.name || "—"}
                        </h2>
                        <p className="text-xs text-[#A3A3A3] mt-0.5">
                            Fix auto-fill mistakes. Only fields you change are updated.
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="edit-candidate-close"
                        onClick={onClose}
                        className="p-2 hover:bg-[#F5F5F5]"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <form onSubmit={save} className="p-5 space-y-4">
                    {err && (
                        <div
                            data-testid="edit-candidate-error"
                            className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-3 py-2"
                        >
                            {err}
                        </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block col-span-1 sm:col-span-2">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Full name</span>
                            <input
                                data-testid="edit-cand-name"
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                                placeholder="e.g. Rahul Sharma"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Email</span>
                            <input
                                data-testid="edit-cand-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Phone</span>
                            <input
                                data-testid="edit-cand-phone"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Current title</span>
                            <input
                                data-testid="edit-cand-title"
                                value={form.current_title}
                                onChange={(e) => setForm({ ...form, current_title: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Location</span>
                            <input
                                data-testid="edit-cand-location"
                                value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Years of experience</span>
                            <input
                                data-testid="edit-cand-years"
                                type="number"
                                min={0}
                                step={0.5}
                                value={form.years_experience}
                                onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                            />
                        </label>
                        <label className="block col-span-1 sm:col-span-2">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">LinkedIn URL</span>
                            <input
                                data-testid="edit-cand-linkedin"
                                value={form.linkedin}
                                onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                                className="w-full border border-[#E5E5E5] focus:border-ink focus:ring-0 outline-none px-3 py-2 text-sm"
                                placeholder="https://linkedin.com/in/..."
                            />
                        </label>
                        <div className="col-span-1 sm:col-span-2">
                            <span className="block text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1">Skills</span>
                            <SkillsChipInput
                                value={skills}
                                onChange={setSkills}
                                testId="edit-cand-skills"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E5E5]">
                        <button
                            type="button"
                            data-testid="edit-cand-cancel"
                            onClick={onClose}
                            className="border border-[#E5E5E5] hover:border-ink text-[#525252] hover:text-ink px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="edit-cand-save"
                            disabled={saving}
                            className="bg-ink hover:bg-cobalt disabled:opacity-60 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
                        >
                            <FloppyDisk size={12} weight="bold" />
                            {saving ? "Saving…" : "Save changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
